import type {
  APICatalogDiamondsResponse,
  APICatalogFilter,
  APICatalogJewelryResponse,
  ApiCatalogRequest,
  APICatalogSortItem,
  APIDiamondItem,
  APIJewelryItem,
} from '~/api/catalogApi'

import { isBlank } from '~/helpers'
import { fromCatalogApi as makeDiamondItem } from '~/layers/catalog/data/adapters/diamondItemAdapter'
import { fromCatalogApi as makeFilter } from '~/layers/catalog/data/adapters/filterItemAdapter'
import { fromCatalogApi as makeJewelryItem } from '~/layers/catalog/data/adapters/jewelryItemAdapter'
import { fromCatalogApi as makeNavigation } from '~/layers/catalog/data/adapters/navigationAdapter'
import { fromCatalogApi as makeSortItem } from '~/layers/catalog/data/adapters/sortItemAdapter'
import { isCatalogRequestScopeActive } from '~/layers/catalog/data/catalogRequestScope'
import { getJewelryCategoryFromPath } from '~/layers/catalog/data/catalogRoute'
import {
  buildCatalogUrlLocation,
  catalogURL,
} from '~/layers/catalog/data/catalogURL'
import { useCatalogStore } from '~/layers/catalog/store/catalog'
import { useCatalogGridStore } from '~/layers/catalog/store/catalogGrid'

// eslint-disable-next-line @typescript-eslint/naming-convention
export type CatalogDataProviderType = 'jewelry' | 'diamonds'
type TCatalogDataProviderResponse =
  | APICatalogJewelryResponse
  | APICatalogDiamondsResponse
  | {
      filters: APICatalogFilter[]
      sortList: APICatalogSortItem[]
      totalCount?: number
    }
type TCatalogFetchFunc = (
  request: ApiCatalogRequest,
) => Promise<TCatalogDataProviderResponse>

const pending = ref(false)
const pendingMore = ref(false)
const type = ref<CatalogDataProviderType>('jewelry')
const catalogTopEl = ref<HTMLElement | null>(null)
const skipInitialFetch = ref(false)
const baseRequest = ref<ApiCatalogRequest>({})
const baseRequestScope = ref<string | null>(null)
const catalogBasePath = ref<string | null>(null)
const parseCategoryFromUrl = ref(true)
const useCategoryPath = ref(true)
const customFetchFunc = ref<TCatalogFetchFunc | null>(null)
const shouldBuildItems = ref(true)
const shouldFetchAfterRouteApply = ref(true)
const isApplyingRoute = ref(false)

let activePopstateHandler: ((evt: PopStateEvent) => void) | null = null
let activeRouteWatchStop: (() => void) | null = null

export const useCatalogDataProvider = () => {
  const $api = useNuxtApp().$api
  const route = useRoute()
  const store = useCatalogStore()
  const bus = useCatalogEventBus()
  const URL = catalogURL(store.selectedCategories)
  const gridStore = useCatalogGridStore()

  const setType = (t: CatalogDataProviderType): void => {
    type.value = t
  }

  const setBaseRequest = (
    request: ApiCatalogRequest,
    scopePath: string | null = null,
  ): void => {
    baseRequest.value = request
    baseRequestScope.value = scopePath
  }

  const setCustomFetch = (fetchFunc: TCatalogFetchFunc | null): void => {
    customFetchFunc.value = fetchFunc
  }

  const setShouldBuildItems = (value: boolean): void => {
    shouldBuildItems.value = value
  }

  const setShouldFetchAfterRouteApply = (value: boolean): void => {
    shouldFetchAfterRouteApply.value = value
  }

  const setUrlOptions = (options: {
    basePath?: string | null
    parseCategory?: boolean
    useCategoryPath?: boolean
  }): void => {
    catalogBasePath.value = options.basePath ?? null
    parseCategoryFromUrl.value = options.parseCategory ?? true
    useCategoryPath.value = options.useCategoryPath ?? true
  }

  const buildRequest = (): ApiCatalogRequest => {
    const apiRequest: ApiCatalogRequest = isCatalogRequestScopeActive(
      route.path,
      baseRequestScope.value,
    )
      ? { ...baseRequest.value }
      : {}

    if (store.selectedCategories.length) {
      const categoryCodes = store.selectedCategories.filter(Boolean)
      if (categoryCodes.length) {
        if (useCategoryPath.value && categoryCodes.length === 1) {
          apiRequest['category'] = categoryCodes[0]
        } else {
          apiRequest['categories[]'] = categoryCodes
        }
      }
    }

    for (const filter of store.filters) {
      if (filter.payload.type === TFilterStoringType.Options) {
        const payload = filter.payload as TFilterStoringOptions
        apiRequest[filter.code + '[]'] = payload.options
      }
      if (filter.payload.type === TFilterStoringType.Range) {
        const payload = filter.payload as TFilterStoringRange
        const range: TFilterStoringRange['range'] = {
          from: payload.range.from,
          to: payload.range.to,
        }

        if (isBlank(range.from) && isBlank(range.to)) {
          continue
        }

        if (!isBlank(range.from)) {
          apiRequest[filter.code + '[min]'] = range.from
        }

        if (!isBlank(range.to)) {
          apiRequest[filter.code + '[max]'] = range.to
        }
      }
    }

    if (store.sort.code && store.sort.direction) {
      apiRequest.sortBy = store.sort.code
      apiRequest.order = store.sort.direction
    }

    apiRequest.pageSize = 24
    apiRequest.page = store.page

    return apiRequest
  }

  const buildFromResponse = (response: TCatalogDataProviderResponse): void => {
    if ('catalog' in response) {
      if (shouldBuildItems.value) {
        for (const item of response.catalog.items) {
          if (type.value === 'jewelry') {
            gridStore.pushItem(
              makeJewelryItem(
                item as APIJewelryItem,
                !!store.selectedCategories.length,
              ),
            )
          }
          if (type.value === 'diamonds') {
            gridStore.pushItem(makeDiamondItem(item as APIDiamondItem))
          }
        }
      }
    }

    for (const filter of response.filters) {
      gridStore.pushFilter(makeFilter(filter, type.value))
    }

    if (type.value === 'jewelry') {
      gridStore.pushFilter({
        items: [],
        title: 'Украшения POSIÉ',
        code: 'posieBanner',
        tooltip: null,
        isOpened: false,
        disabled: false,
        applyingOrder: 999,
        showInvestmentDiamonds: false,
        showParametersRussianMetricSystem: false,
        type: 0,
      })
    }

    for (const sortItem of response.sortList) {
      gridStore.pushSorting(makeSortItem(sortItem))
    }

    if ('catalog' in response) {
      gridStore.setNavigation(makeNavigation(response.catalog.navigation))
    } else {
      gridStore.setNavigation({
        page: store.page,
        perPage: 0,
        total: response.totalCount ?? 0,
        lastPage: 1,
        isLastPage: true,
      })
    }

    if (
      store.selectedCategories.length === 1 &&
      !!store.selectedCategories[0] &&
      type.value === 'jewelry'
    ) {
      gridStore.setPageTitle(
        store.categories.find((c) => c.code === store.selectedCategories[0])
          ?.name || 'Украшения',
      )
    } else if (type.value === 'jewelry') {
      gridStore.setPageTitle('Украшения')
    } else if (type.value === 'diamonds') {
      gridStore.setPageTitle('Бриллианты')
    }
  }

  const fetch = async (loadMore = false) => {
    if (pending.value || pendingMore.value) {
      return
    }

    if (!loadMore) {
      pending.value = true
    } else {
      pendingMore.value = true
    }

    const apiRequest = buildRequest()

    const fetchFunc =
      customFetchFunc.value ??
      (type.value === 'jewelry'
        ? $api.catalog.catalog
        : $api.catalog.catalogDiamonds)

    try {
      const response = await fetchFunc(apiRequest)

      // Clear prev data
      gridStore.clearFilters()
      gridStore.clearSorting()
      if (!loadMore) {
        gridStore.clearItems()
      }

      // Build Data from response
      buildFromResponse(response)

      return response
    } finally {
      pending.value = false
      pendingMore.value = false
    }
  }

  const scrollToCatalogTop = () => {
    const grid = catalogTopEl.value
    if (!import.meta.client || !grid) {
      return
    }

    const filters = grid
      .closest('.catalog__container')
      ?.querySelector('.catalog__header-wrapper')
    const filtersStyle = filters ? window.getComputedStyle(filters) : null
    let offset =
      document.querySelector('.header')?.getBoundingClientRect().bottom ?? 0

    if (filters && filtersStyle?.position === 'sticky') {
      offset =
        (Number.parseFloat(filtersStyle.top) || 0) +
        filters.getBoundingClientRect().height
    }

    window.scrollTo({
      top: window.scrollY + grid.getBoundingClientRect().top - offset,
      behavior: 'smooth',
    })
  }

  const getTypeFromRoute = (): CatalogDataProviderType => {
    if (typeof window !== 'undefined') {
      return window.location.pathname.startsWith('/diamonds')
        ? 'diamonds'
        : 'jewelry'
    } else {
      const route = useRoute()
      return route.fullPath.startsWith('/diamonds') ? 'diamonds' : 'jewelry'
    }
  }

  const isCurrentCatalogRoute = (fullPath: string): boolean => {
    if (!catalogBasePath.value) {
      if (type.value === 'diamonds') {
        return fullPath.startsWith('/diamonds/')
      }

      return fullPath.startsWith('/jewelry/')
    }

    const basePath = catalogBasePath.value.endsWith('/')
      ? catalogBasePath.value
      : `${catalogBasePath.value}/`
    if (!fullPath.startsWith(basePath)) {
      return false
    }

    const restPath = fullPath.slice(basePath.length)
    return (
      !restPath ||
      restPath.startsWith('filter/') ||
      /^\d+(?:\/|$)/.test(restPath)
    )
  }

  const getFilterPathWithoutPage = (fullPath: string): string => {
    const path = fullPath.split(/[?#]/)[0]
    const filterPath = path.split('/filter/')[1] ?? ''

    return filterPath
      .split('/')
      .filter((segment) => segment && !/^\d+$/.test(segment))
      .join('/')
  }

  const popstateHandler = async (_evt: PopStateEvent) => {
    scrollToCatalogTop()
    setType(getTypeFromRoute())
  }

  const loadMoreHandler = async () => {
    skipInitialFetch.value = true
    try {
      await URL.apply(gridStore.filters, type.value, {
        basePath: catalogBasePath.value ?? undefined,
        useCategoryPath: useCategoryPath.value,
      })
      await fetch(true)
    } finally {
      skipInitialFetch.value = false
      if (import.meta.client) {
        await nextTick()
        useLoadingIndicator().finish()
      }
    }
  }

  const getPageHref = (page: number): string | null => {
    if (
      route.path.includes('/filter/') ||
      (!useCategoryPath.value && store.selectedCategories.length)
    ) {
      return null
    }

    const location = buildCatalogUrlLocation(
      gridStore.filters,
      type.value,
      {
        ...store,
        filters: [],
      },
      {
        basePath: catalogBasePath.value ?? undefined,
        page,
        useCategoryPath: useCategoryPath.value,
      },
    )
    const searchParams = new URLSearchParams(location.query)
    const query = searchParams.toString()

    return query ? `${location.path}?${query}` : location.path
  }

  const init = async () => {
    unload()

    await URL.parse({ parseCategory: parseCategoryFromUrl.value })

    const tableSortCodes = new Set(['weight', 'color', 'clarity', 'price'])
    if (
      type.value === 'diamonds' &&
      store.sort.code &&
      tableSortCodes.has(store.sort.code)
    ) {
      store.resetSort()

      if (import.meta.client && route.query.sortBy) {
        const query = { ...route.query }
        delete query.sortBy
        delete query.order
        await useRouter().replace({ query })
      }
    }

    const updateHandler = async () => {
      isApplyingRoute.value = true
      skipInitialFetch.value = shouldFetchAfterRouteApply.value
      try {
        await URL.apply(gridStore.filters, type.value, {
          basePath: catalogBasePath.value ?? undefined,
          useCategoryPath: useCategoryPath.value,
        })
        if (shouldFetchAfterRouteApply.value) {
          await fetch()
        }
      } finally {
        isApplyingRoute.value = false
        skipInitialFetch.value = false
        if (import.meta.client) {
          await nextTick()
          useLoadingIndicator().finish()
        }
      }
    }

    if (import.meta.client) {
      if (activePopstateHandler && activePopstateHandler !== popstateHandler) {
        window.removeEventListener('popstate', activePopstateHandler)
      }
      window.addEventListener('popstate', popstateHandler)
      activePopstateHandler = popstateHandler

      activeRouteWatchStop?.()
      activeRouteWatchStop = watch(
        () => route.fullPath,
        async (newPath, oldPath) => {
          const decodedNewPath = decodeURI(newPath)

          if (!isCurrentCatalogRoute(decodedNewPath)) {
            return
          }

          if (isApplyingRoute.value && shouldFetchAfterRouteApply.value) {
            return
          }

          const newSegments = newPath.split('/')
          const oldSegments = oldPath.split('/')
          const newCollectionPart = newSegments.find((segment) =>
            segment.startsWith('collection'),
          )
          const oldCollectionPart = oldSegments.find((segment) =>
            segment.startsWith('collection'),
          )
          const newFilterPath = getFilterPathWithoutPage(newPath)
          const oldFilterPath = getFilterPathWithoutPage(oldPath)
          const newCategory = getJewelryCategoryFromPath(newPath)
          const oldCategory = getJewelryCategoryFromPath(oldPath)

          if (
            newCollectionPart !== oldCollectionPart ||
            newFilterPath !== oldFilterPath ||
            newCategory !== oldCategory
          ) {
            await URL.parse({ parseCategory: parseCategoryFromUrl.value })
            await fetch()

            await nextTick()
            useLoadingIndicator().finish()
          }
        },
      )
    }

    bus.on('category:updated', async () => {
      await fetch()
    })
    bus.on('filter:updated', async () => {
      store.setPage(1)
      store.setInitialPage(1)
      await updateHandler()
    })
    bus.on('sort:updated', async () => {
      store.setPage(1)
      store.setInitialPage(1)
      await updateHandler()
    })
    bus.on('page:updated', async () => {
      await updateHandler()
      await nextTick()
      scrollToCatalogTop()
    })
    bus.on('page:more', loadMoreHandler)
  }

  const unload = () => {
    store.resetFilters()

    bus.off('category:updated')
    bus.off('filter:updated')
    bus.off('sort:updated')
    bus.off('page:updated')
    bus.off('page:more')

    if (import.meta.client) {
      if (activePopstateHandler === popstateHandler) {
        window.removeEventListener('popstate', popstateHandler)
        activePopstateHandler = null
      }

      if (activeRouteWatchStop) {
        activeRouteWatchStop()
        activeRouteWatchStop = null
      }
    }
  }

  return {
    pending,
    pendingMore,
    init,
    fetch,
    unload,
    setType,
    setBaseRequest,
    setCustomFetch,
    setShouldBuildItems,
    setShouldFetchAfterRouteApply,
    setUrlOptions,
    type,
    catalogTopEl,
    skipInitialFetch,
    getPageHref,
  }
}
